import { defineStore } from 'pinia';
import { reactive, ref } from 'vue';
import client from '@/api/client';

/**
 * O(1) "is this listing favorited" lookups for every SkinCard on the page.
 *
 * State shape:
 *   ids       — Set<listingId>; the authoritative client-side cache
 *   idsLoaded — false until the first GET /favorites/ids resolves; the
 *               watcher in App.vue checks this to avoid re-fetching on every
 *               navigation
 *   pending   — Set<listingId> currently in-flight; used to gate the heart
 *               button so a rapid double-click can't desync the set
 *
 * Optimistic toggle pattern: flip the set first, fire the network call, roll
 * back on failure. Keeps the heart icon responsive while still keeping the
 * server-side source of truth canonical.
 */
export const useFavoritesStore = defineStore('favorites', () => {
  const ids = reactive(new Set<string>());
  const idsLoaded = ref(false);
  const pending = reactive(new Set<string>());

  async function loadIds(): Promise<void> {
    try {
      const { data } = await client.get<string[]>('/favorites/ids');
      ids.clear();
      for (const id of data) ids.add(id);
      idsLoaded.value = true;
    } catch {
      // Anonymous users hit /favorites/ids and get 401; that's fine — we just
      // never set idsLoaded, the App.vue watcher will retry once logged in.
    }
  }

  function reset(): void {
    ids.clear();
    pending.clear();
    idsLoaded.value = false;
  }

  function isFavorite(listingId: string): boolean {
    return ids.has(listingId);
  }

  function isPending(listingId: string): boolean {
    return pending.has(listingId);
  }

  /**
   * Add or remove based on current state. Optimistic — UI flips immediately,
   * rolls back if the server rejects. Concurrent toggles on the same listing
   * are coalesced via the `pending` set so a fast double-click can't end up
   * with stale state.
   */
  async function toggle(listingId: string): Promise<void> {
    if (pending.has(listingId)) return;
    pending.add(listingId);

    const wasFavorite = ids.has(listingId);
    if (wasFavorite) ids.delete(listingId);
    else ids.add(listingId);

    try {
      if (wasFavorite) {
        await client.delete(`/favorites/${listingId}`);
      } else {
        await client.post(`/favorites/${listingId}`);
      }
    } catch (err) {
      // Roll back optimistic flip.
      if (wasFavorite) ids.add(listingId);
      else ids.delete(listingId);
      throw err;
    } finally {
      pending.delete(listingId);
    }
  }

  return {
    ids,
    idsLoaded,
    pending,
    loadIds,
    reset,
    isFavorite,
    isPending,
    toggle,
  };
});
